/**
 * Fill in the Blanks Content Type Schema
 * A content type with title/description display and fill-in-the-blanks questions
 */

import { generateId } from '@ai-content-hub/content-core';
import type {
    CompositeQuestionsLayoutFields,
    ContentConfig,
    LayoutConfig,
} from '@ai-content-hub/content-core';
import type { FITBElementData, FITBBlankMode, PageElementData } from '@ai-content-hub/content-elements';
import {
    defaultFITBConfig,
    defaultFITBElementData,
    defaultPageData,
} from '@ai-content-hub/content-elements';

/**
 * Stable id for the exercise row synthesized when `exercises` is empty.
 * Must not use {@link generateId} here: normalize runs every render and would
 * otherwise produce a new id each time (infinite setState loops in editors).
 */
const FITB_CONTENT_DEFAULT_EXERCISE_ID = 'a0000000-0000-4000-8000-000000000001';

/**
 * Player view layout
 * - stacked: Title/description on top, fill-in-the-blanks below (default)
 * - compact: Title inline, blanks below
 */
export type FITBPlayerView = 'stacked' | 'compact';

/**
 * Layout configuration for FITB content type
 */
export interface FITBContentLayoutConfig extends LayoutConfig {
    /** Player view layout */
    playerView?: FITBPlayerView;
}

/**
 * FITB content type config — extends the element's FITBConfig
 */
export interface FITBContentConfig extends ContentConfig {
    layout?: FITBContentLayoutConfig;
    /** Blank input mode: 'drag' | 'dropdown' | 'type' */
    blankMode?: FITBBlankMode;
    /** Whether blank matching is case sensitive */
    caseSensitive?: boolean;
    /** Whether to shuffle options in drag/dropdown mode */
    shuffleOptions?: boolean;
}

/**
 * One fill-in-the-blanks passage / exercise in the Questions tab
 */
export interface FITBContentExercise {
    id: string;
    data: FITBElementData;
}

/**
 * FITB content type data — wraps FITB element(s) with title/description
 */
export interface FITBContentData extends CompositeQuestionsLayoutFields {
    /** Title displayed above the exercise */
    title?: string;
    /** Description / instructions */
    description?: string;
    /** Intro / lesson blocks shown before the blanks (editor Content tab) */
    lessonContent: PageElementData;
    /** One or more blank exercises (Questions tab) */
    exercises: FITBContentExercise[];
    /**
     * Legacy single exercise — normalized into `exercises` on read.
     * Do not set when saving; use `exercises` only.
     */
    fitbData?: FITBElementData;
}

/**
 * Complete FITB content item
 */
export interface FITBContent {
    id: string;
    type: 'fill-in-the-blanks';
    version: string;
    data: FITBContentData;
    config?: FITBContentConfig;
}

/**
 * Default i18n translations
 */
export const defaultFITBContentI18n = {
    en: {
        title: 'Fill in the Blanks',
        instructions: 'Complete the passage by filling in the blanks',
        submit: 'Submit',
        retry: 'Try Again',
        score: 'Score',
        passed: 'Passed!',
        failed: 'Keep practicing',
        contentTab: 'Content',
        questionsTab: 'Questions',
        configTab: 'Configuration',
        titleLabel: 'Title',
        titlePlaceholder: 'Enter title',
        descLabel: 'Instructions (Optional)',
        descPlaceholder: 'Enter instructions...',
        questionsListTitle: 'Questions',
        emptyQuestionsMsg: 'No questions yet. Add a fill-in-the-blanks exercise.',
        addSection: 'Add section',
        removeSection: 'Remove section',
        sectionDetailTitle: 'Question section',
        addExerciseLabel: 'Add exercise',
        addExerciseOption: 'Fill in the blanks',
        detailEmptyMessage: 'Select a question, a section, or add one to start.',
        exercisePreview: 'Question {{index}} — {{n}} blanks',
        questionNumber: 'Question {{index}}',
    },
    vi: {
        title: 'Điền vào chỗ trống',
        instructions: 'Hoàn thành đoạn văn bằng cách điền vào chỗ trống',
        submit: 'Gửi câu trả lời',
        retry: 'Thử lại',
        score: 'Điểm',
        passed: 'Đã vượt qua!',
        failed: 'Tiếp tục luyện tập',
        contentTab: 'Nội dung',
        questionsTab: 'Câu hỏi',
        configTab: 'Cấu hình',
        titleLabel: 'Tiêu đề',
        titlePlaceholder: 'Nhập tiêu đề bài tập điền vào chỗ trống',
        descLabel: 'Hướng dẫn bài tập (Tuỳ chọn)',
        descPlaceholder: 'Nhập hướng dẫn bài tập...',
        questionsListTitle: 'Câu hỏi',
        emptyQuestionsMsg: 'Chưa có câu. Thêm câu điền khuyết.',
        addSection: 'Thêm nhóm',
        removeSection: 'Xóa nhóm',
        sectionDetailTitle: 'Nhóm câu hỏi',
        addExerciseLabel: 'Thêm câu điền khuyết',
        addExerciseOption: 'Điền vào chỗ trống',
        detailEmptyMessage: 'Chọn một câu, một nhóm, hoặc thêm mới.',
        exercisePreview: 'Câu {{index}} — {{n}} chỗ trống',
        questionNumber: 'Câu {{index}}',
    },
};

/**
 * Default config
 */
export const defaultFITBContentConfig: FITBContentConfig = {
    layout: {
        type: 'vertical',
        playerView: 'stacked',
    },
    blankMode: defaultFITBConfig.blankMode,
    caseSensitive: defaultFITBConfig.caseSensitive,
    shuffleOptions: defaultFITBConfig.shuffleOptions,
    behavior: {
        allowRetry: true,
        showCorrectAnswer: true,
        showExplanation: true,
    },
    scoring: {
        partialCreditEnabled: true,
    },
    i18n: {
        defaultLanguage: 'en',
        languages: defaultFITBContentI18n,
    },
};

/**
 * Default data (exercises may be empty until editor normalizes)
 */
export const defaultFITBContentData: FITBContentData = {
    title: '',
    description: '',
    lessonContent: { ...defaultPageData },
    exercises: [],
};

/**
 * Merge legacy `fitbData` and ensure at least one exercise with stable structure.
 */
export function normalizeFITBContentData(
    data: Partial<FITBContentData> | null | undefined,
): FITBContentData {
    const lessonContent = data?.lessonContent ?? { ...defaultPageData };
    let exercises = data?.exercises;
    if (!exercises?.length) {
        if (data?.fitbData) {
            exercises = [{ id: FITB_CONTENT_DEFAULT_EXERCISE_ID, data: data.fitbData }];
        } else {
            exercises = [
                {
                    id: FITB_CONTENT_DEFAULT_EXERCISE_ID,
                    data: { ...defaultFITBElementData },
                },
            ];
        }
    } else {
        exercises = exercises.map((e) => ({
            id: e.id?.trim() ? e.id : generateId(),
            data: e.data ?? { ...defaultFITBElementData },
        }));
    }
    const questionsSidebarRows = data?.questionsSidebarRows;
    return {
        title: data?.title ?? '',
        description: data?.description ?? '',
        lessonContent,
        exercises,
        ...(Array.isArray(questionsSidebarRows) && questionsSidebarRows.length > 0
            ? { questionsSidebarRows }
            : {}),
    };
}

/**
 * Strip legacy fields for persistence
 */
export function serializableFITBContentData(data: FITBContentData): FITBContentData {
    const n = normalizeFITBContentData(data);
    return {
        title: n.title,
        description: n.description,
        lessonContent: n.lessonContent,
        exercises: n.exercises,
        ...(n.questionsSidebarRows?.length ? { questionsSidebarRows: n.questionsSidebarRows } : {}),
    };
}

/**
 * Schema definition
 */
export const fitbContentSchema = {
    type: 'fill-in-the-blanks' as const,
    title: 'Fill in the Blanks',
    description:
        'Passage with blanks — drag-and-drop, dropdown, or type answers with title and instructions',
    version: '1.0.0',
    category: 'assessment' as const,
    icon: 'TextCursorInput',
    tags: ['fitb', 'blanks', 'cloze', 'assessment'],
    defaultData: defaultFITBContentData,
    defaultConfig: defaultFITBContentConfig,
};
